import React, { useState, useEffect } from 'react';
import { Link, useLocation } from 'react-router-dom';
import { Menu, X } from 'lucide-react';
import { Button } from './ui/button';
import { trackButtonClick } from '../utils/analytics';

const Header = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const navLinks = [
    { path: '/', label: 'Home' },
    { path: '/services', label: 'Services' },
    { path: '/partner-program', label: 'Partner Program' },
    { path: '/pricing', label: 'Pricing' },
    { path: '/about', label: 'About Us' },
    { path: '/contact', label: 'Contact Us' },
    { path: '/careers', label: 'Careers' }
  ];

  const handleNavClick = (label) => {
    trackButtonClick(`nav_${label.toLowerCase().replace(' ', '_')}`, 'header');
    setIsMobileMenuOpen(false);
  };

  const handleCTAClick = () => {
    trackButtonClick('request_demo', 'header');
  };

  return (
    <header
      className="fixed top-0 left-0 right-0 z-50 bg-white shadow-md transition-all duration-300"
    >
      <div className="max-w-[1280px] mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-20">
          {/* Logo */}
          <Link to="/" onClick={() => handleNavClick('logo')} className="flex-shrink-0">
            <img
              src="https://tally-bharat.s3.ap-south-1.amazonaws.com/tb-logo.png"
              alt="TallyBharat Logo"
              className="h-12 w-auto"
            />
          </Link>

          {/* Desktop Navigation */}
          <nav className="hidden lg:flex items-center space-x-1">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => handleNavClick(link.label)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-200 relative group ${
                  location.pathname === link.path
                    ? 'text-[#0056D2] font-semibold'
                    : 'text-[#0A1F44] hover:text-[#0056D2]'
                }`}
              >
                {link.label}
                <span
                  className={`absolute bottom-0 left-0 w-full h-0.5 bg-[#0056D2] transform origin-left transition-transform duration-200 ${
                    location.pathname === link.path
                      ? 'scale-x-100'
                      : 'scale-x-0 group-hover:scale-x-100'
                  }`}
                />
              </Link>
            ))}
          </nav>

          {/* CTA Buttons - Desktop */}
          <div className="hidden lg:flex items-center space-x-3">
            <a href="https://app.tallybharat.com/login" target="_blank" rel="noopener noreferrer">
              <Button
                onClick={() => trackButtonClick('login', 'header')}
                variant="outline"
                className="border-2 border-[#0056D2] text-[#0056D2] hover:bg-[#0056D2] hover:text-white font-semibold px-5 py-2 rounded-full transition-all duration-200"
              >
                Login
              </Button>
            </a>
            <a href="https://app.tallybharat.com/signup" target="_blank" rel="noopener noreferrer">
              <Button
                onClick={() => trackButtonClick('signup', 'header')}
                className="bg-[#FF7A00] hover:bg-[#e66d00] text-white font-semibold px-5 py-2 rounded-full transition-all duration-200 hover:scale-105 hover:shadow-lg"
              >
                Sign Up
              </Button>
            </a>
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
            className="lg:hidden p-2 rounded-lg text-[#222B45] hover:bg-[#F5F7FA] transition-colors"
            aria-label="Toggle menu"
          >
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      {isMobileMenuOpen && (
        <div className="lg:hidden bg-white border-t border-gray-100 shadow-xl">
          <nav className="px-4 py-6 space-y-2">
            {navLinks.map((link) => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => handleNavClick(link.label)}
                className={`block px-4 py-3 rounded-lg text-base font-medium transition-colors ${
                  location.pathname === link.path
                    ? 'bg-[#0056D2] text-white'
                    : 'text-[#222B45] hover:bg-[#F5F7FA]'
                }`}
              >
                {link.label}
              </Link>
            ))}
            <div className="pt-4 space-y-3">
              <a href="https://app.tallybharat.com/login" target="_blank" rel="noopener noreferrer" className="block">
                <Button
                  onClick={() => trackButtonClick('login', 'mobile_menu')}
                  variant="outline"
                  className="w-full border-2 border-[#0056D2] text-[#0056D2] hover:bg-[#0056D2] hover:text-white font-semibold py-3 rounded-full"
                >
                  Login
                </Button>
              </a>
              <a href="https://app.tallybharat.com/signup" target="_blank" rel="noopener noreferrer" className="block">
                <Button
                  onClick={() => trackButtonClick('signup', 'mobile_menu')}
                  className="w-full bg-[#FF7A00] hover:bg-[#e66d00] text-white font-semibold py-3 rounded-full"
                >
                  Sign Up
                </Button>
              </a>
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Header;